import React, { useEffect, useMemo, useRef, useState } from "react";
import { Chess } from "chess.js";
import { api } from "../lib/api";
import { formatThemeLabel, formatStageError } from "../lib/format";
import { analysePuzzle } from "../lib/gameReview";
import Board from "../components/Board";
import { Sounds } from "../lib/sounds";
import { useClickToMove } from "../lib/useClickToMove";
import { toast } from "sonner";
import { Eye, ArrowRight, Lightbulb, BookOpen } from "lucide-react";

/*
 * Lichess puzzle format:
 *  - fen: position BEFORE the opponent plays the setup move
 *  - moves[0]: opponent's setup move (auto-played)
 *  - moves[1..]: solution (user & opponent alternate)
 */

export default function Puzzles() {
  const [puzzle, setPuzzle] = useState(null);
  const [chess, setChess] = useState(null);
  const [expected, setExpected] = useState([]); // remaining SOLUTION uci moves (user is next)
  const [status, setStatus] = useState("loading"); // loading|playing|solved|revealed|failed
  const [rating, setRating] = useState([1000, 1600]);
  const [theme, setTheme] = useState("");
  const [themes, setThemes] = useState([]);
  const [flash, setFlash] = useState({}); // transient square highlight (last move / feedback)
  const [wrongTries, setWrongTries] = useState(0);
  const [usage, setUsage] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [explanation, setExplanation] = useState(null); // { best_move_san, solution_san[], explanation }
  const [explainLoading, setExplainLoading] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    api.get("/puzzles/themes").then(({ data }) => setThemes(data.themes || []));
  }, []);

  const orientation = useMemo(() => {
    if (!puzzle) return "white";
    try {
      const c = new Chess(puzzle.fen);
      c.move({ from: puzzle.moves[0].slice(0, 2), to: puzzle.moves[0].slice(2, 4), promotion: puzzle.moves[0][4] });
      return c.turn() === "w" ? "white" : "black";
    } catch {
      return "white";
    }
  }, [puzzle]);

  const solStartNum = useMemo(() => {
    if (!puzzle) return 1;
    try {
      const c = new Chess(puzzle.fen);
      c.move({ from: puzzle.moves[0].slice(0, 2), to: puzzle.moves[0].slice(2, 4), promotion: puzzle.moves[0][4] });
      return Number(c.fen().split(" ")[5]) || 1;
    } catch {
      return 1;
    }
  }, [puzzle]);
  const load = async () => {
    setStatus("loading");
    setFlash({});
    setErrorMsg("");
    setWrongTries(0);
    setExplanation(null);
    try {
      const params = { rating_min: rating[0], rating_max: rating[1] };
      if (theme) params.theme = theme;
      const { data } = await api.get("/puzzles/random", { params });
      setPuzzle(data);
      setUsage(data.usage);
      const c = new Chess(data.fen);
      const opp = data.moves[0];
      c.move({ from: opp.slice(0, 2), to: opp.slice(2, 4), promotion: opp[4] });
      setChess(c);
      setExpected([...data.moves.slice(1)]);
      setStatus("playing");
      setFlash({
        [opp.slice(0, 2)]: { background: "rgba(198,161,91,0.35)" },
        [opp.slice(2, 4)]: { background: "rgba(198,161,91,0.5)" },
      });
      Sounds.move();
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || "No puzzles found.");
      setStatus("failed");
    }
  };

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    load();
    /* eslint-disable-next-line */
  }, []);

  const themeRef = useRef(theme);
  useEffect(() => {
    if (themeRef.current === theme) return;
    themeRef.current = theme;
    load();
    /* eslint-disable-next-line */
  }, [theme]);

  const applyExpectedMove = (currentChess, uci) => {
    const c = new Chess(currentChess.fen());
    const legal = c.moves({ verbose: true }).find(
      (m) => m.from === uci.slice(0, 2) && m.to === uci.slice(2, 4) && (uci.length < 5 || m.promotion === uci[4]),
    );
    if (!legal) return { chess: c, isCapture: false, san: null };
    c.move({ from: legal.from, to: legal.to, promotion: legal.promotion || uci[4] || "q" });
    return { chess: c, isCapture: !!legal.captured, san: legal.san };
  };

  const attempt = (from, to, piece, isCapture, promotion) => {
    if (status !== "playing") return false;
    const uci = from + to + (promotion || "");
    const expected0 = expected[0];
    // Exact UCI match required (including exact promotion piece if any).
    // Also accept the case where the user's move has no promo suffix but
    // the solution's promo default is queen ('q').
    const matches =
      uci === expected0 ||
      (uci.length === 4 && expected0.length === 5 && expected0[4] === "q" && uci === expected0.slice(0, 4));

    if (!matches) {
      // wrong
      Sounds.wrong();
      setWrongTries((w) => w + 1);
      setFlash({
        [from]: { background: "rgba(160,74,60,0.5)" },
        [to]: { background: "rgba(160,74,60,0.6)" },
      });
      toast.error("Not quite — try again");
      // clear red flash after a moment (keep last-move visible optionally)
      setTimeout(() => setFlash({}), 900);
      return false;
    }

    // correct user move — apply on real board
    const { chess: c1, isCapture: cap } = applyExpectedMove(chess, expected0);
    if (cap) Sounds.capture(); else Sounds.move();
    setFlash({
      [from]: { background: "rgba(92,135,85,0.4)" },
      [to]: { background: "rgba(92,135,85,0.55)" },
    });
    let rest = expected.slice(1);

    if (rest.length === 0) {
      setChess(c1);
      setExpected([]);
      setStatus("solved");
      api.post("/puzzles/attempt", { puzzle_id: puzzle.id, solved: true }).catch(() => {});
      setTimeout(() => Sounds.correct(), 200);
      toast.success("Solved!");
      return true;
    }

    // multi-move puzzle: intermediate correct move — confirm and continue
    toast.success("Correct — keep going", { duration: 1500 });

    // auto-play opponent reply after brief delay
    setTimeout(() => {
      const { chess: c2, isCapture: cap2 } = applyExpectedMove(c1, rest[0]);
      if (cap2) Sounds.capture(); else Sounds.move();
      rest = rest.slice(1);
      setChess(c2);
      setExpected(rest);
      setFlash({
        [expected0.slice(2, 4)]: { background: "rgba(92,135,85,0.35)" },
      });
      if (rest.length === 0) {
        setStatus("solved");
        api.post("/puzzles/attempt", { puzzle_id: puzzle.id, solved: true }).catch(() => {});
        setTimeout(() => Sounds.correct(), 200);
        toast.success("Solved!");
      }
    }, 350);

    setChess(c1);
    setExpected(rest);
    return true;
  };

  const clickToMove = useClickToMove(chess, attempt, status === "playing");

  const showSolution = async () => {
    if (!chess || expected.length === 0) return;
    // record as unsolved
    api.post("/puzzles/attempt", { puzzle_id: puzzle.id, solved: false }).catch(() => {});
    setStatus("revealed");
    // play out remaining solution with a delay between moves
    let current = chess;
    let queue = [...expected];
    for (let i = 0; i < queue.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 550));
      const { chess: next, isCapture } = applyExpectedMove(current, queue[i]);
      if (isCapture) Sounds.capture(); else Sounds.move();
      setFlash({
        [queue[i].slice(0, 2)]: { background: "rgba(235,177,91,0.35)" },
        [queue[i].slice(2, 4)]: { background: "rgba(235,177,91,0.55)" },
      });
      setChess(next);
      current = next;
    }
    setExpected([]);
    setTimeout(() => Sounds.correct(), 200);
    toast("Solution shown", { icon: "\u{1F441}" });
  };

  const fetchExplanation = async () => {
    if (!puzzle || explanation) return;
    setExplainLoading(true);
    let engineData = {};
    try {
      const c = new Chess(puzzle.fen);
      c.move({ from: puzzle.moves[0].slice(0, 2), to: puzzle.moves[0].slice(2, 4), promotion: puzzle.moves[0][4] });
      engineData = await analysePuzzle(c.fen(), puzzle.moves[1]);
    } catch {
      engineData = {};
    }
    try {
      const { data } = await api.post("/puzzles/explain", { puzzle_id: puzzle.id, ...engineData });
      setExplanation(data);
    } catch (e) {
      toast.error(formatStageError(e, "Could not load explanation."));
    } finally {
      setExplainLoading(false);
    }
  };

  const combinedStyles = { ...flash, ...clickToMove.squareStyles };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="overline mb-2">Infinite puzzles</div>
      <h1 className="font-serif text-3xl sm:text-4xl text-[#EFE8D8] mb-6">Solve.</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          {status === "loading" && <div className="p-12 text-center text-[#A8A196]">Loading…</div>}
          {status === "failed" && errorMsg && (
            <div className="card-panel p-6">
              <p className="text-sm text-[#A04A3C]" data-testid="puzzle-error">{errorMsg}</p>
            </div>
          )}
          {chess && (
            <Board
              position={chess.fen()}
              onPieceDrop={clickToMove.onPieceDrop}
              onSquareClick={clickToMove.onSquareClick}
              boardOrientation={orientation}
              arePiecesDraggable={status === "playing"}
              customSquareStyles={combinedStyles}
            />
          )}
        </div>

        <div className="lg:col-span-4 space-y-4">
          <div className="card-panel p-4">
            <div className="overline mb-2">Puzzle</div>
            {puzzle && (
              <div className="text-sm font-mono text-[#EFE8D8] space-y-1">
                <div>Rating: <span className="text-[#C6A15B]">{puzzle.rating}</span></div>
                <div>You play: <span className="text-[#C6A15B]">{orientation}</span></div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(puzzle.themes || []).slice(0, 5).map((t) => (
                    <span key={t} data-testid={`puzzle-theme-chip-${t}`} className="text-[10px] px-2 py-0.5 border border-[#C6A15B]/30 text-[#A8A196] rounded-sm">{formatThemeLabel(t)}</span>
                  ))}
                </div>
              </div>
            )}
            {usage?.limit != null && (
              <div className="mt-3 text-xs text-[#756F67] font-mono">Puzzles solved today: {usage.used + 1}</div>
            )}
            {status === "playing" && wrongTries === 0 && (
              <div className="mt-4 text-xs text-[#A8A196] flex items-center gap-2">
                <Lightbulb size={12} className="text-[#C6A15B]" />
                Click a piece to see legal moves.
              </div>
            )}
            {status === "solved" && (
              <div data-testid="puzzle-solved" className="mt-4 text-sm text-[#5C8755] font-mono">
                ✓ Solved
              </div>
            )}
            {status === "revealed" && (
              <div data-testid="puzzle-revealed" className="mt-4 text-sm text-[#C6A15B] font-mono">
                Solution shown
              </div>
            )}
            {status === "playing" && wrongTries >= 1 && (
              <button
                data-testid="puzzle-show-solution"
                onClick={showSolution}
                className="mt-4 btn-ghost w-full justify-center text-sm"
              >
                <Eye size={14} /> Show the answer
              </button>
            )}
            {(status === "solved" || status === "revealed") && (
              <>
                <button
                  data-testid="puzzle-explain"
                  onClick={fetchExplanation}
                  disabled={explainLoading || !!explanation}
                  className="mt-4 btn-ghost w-full justify-center text-sm"
                >
                  <BookOpen size={14} /> {explainLoading ? "Consulting engine…" : explanation ? "Explanation loaded" : "Explain this puzzle"}
                </button>
                <button
                  data-testid="puzzle-next"
                  onClick={load}
                  className="mt-3 btn-brass w-full justify-center"
                >
                  Next puzzle <ArrowRight size={14} />
                </button>
              </>
            )}
          </div>

          {explanation && (
            <div className="card-panel p-4" data-testid="puzzle-explanation">
              <div className="overline mb-2">Why it works</div>
              <p className="text-sm text-[#EFE8D8] leading-relaxed mb-3" data-testid="puzzle-explanation-text">
                {explanation.explanation}
              </p>
              {explanation.engine_available === false && (
                <p className="text-xs text-[#A8A196] mb-3" data-testid="puzzle-engine-notice">
                  The engine couldn&apos;t run in your browser — showing the verified puzzle solution only.
                </p>
              )}
              {explanation.solution_san?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#756F67] font-mono mb-1">
                    Solution line
                  </div>
                  <div className="font-mono text-xs text-[#C6A15B]" data-testid="puzzle-explanation-line">
                    {explanation.solution_san.map((san, i) => {
                      const blackFirst = orientation === "black";
                      const j = i + (blackFirst ? 1 : 0);
                      const num = solStartNum + Math.floor(j / 2);
                      const prefix = i === 0 && blackFirst ? `${num}...` : j % 2 === 0 ? `${num}.` : "";
                      return (
                        <span key={i} className="inline-block mr-2">
                          {prefix} {san}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {explanation.engine_pv_san?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#756F67] font-mono mb-1">Engine&apos;s line</div>
                  <div className="font-mono text-xs text-[#A8A196]" data-testid="puzzle-engine-line">
                    {explanation.engine_pv_san.join(" ")}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="card-panel p-4">
            <div className="overline mb-3">Filters</div>
            <label className="block text-xs uppercase tracking-widest text-[#756F67] mb-1 font-mono">Theme</label>
            <select data-testid="puzzle-theme" value={theme} onChange={(e) => setTheme(e.target.value)} className="input-ink">
              <option value="">Any</option>
              {themes.map((t) => <option key={t} value={t}>{formatThemeLabel(t)}</option>)}
            </select>
            <label className="block text-xs uppercase tracking-widest text-[#756F67] mt-3 mb-1 font-mono">Rating</label>
            <div className="flex gap-2">
              <input type="number" data-testid="puzzle-rating-min" value={rating[0]} onChange={(e) => setRating([Number(e.target.value), rating[1]])} className="input-ink" />
              <input type="number" data-testid="puzzle-rating-max" value={rating[1]} onChange={(e) => setRating([rating[0], Number(e.target.value)])} className="input-ink" />
            </div>
            <button data-testid="puzzle-skip" onClick={load} className="btn-ghost mt-4 w-full justify-center text-sm">Skip puzzle</button>
          </div>
        </div>
      </div>
    </div>
  );
}
